import React from "react";
import UnclaimButton from "@components/FlowActionButtons/UnclaimButton";
import { useNotification } from "@context/NotificationContext";
import { unclaimMovementById } from "@services/movementServicesApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { canUserUnclaim } from "./utils";

interface UnclaimMovementProps {
  movementFlowData?: {
    id: string;
    task: { status: string; claimOwnerName: string } | null;
    status: string;
    terminalReport: { report: string } | null;
    to: string;
    from: string;
  };
}

const LANG_BASE = "vessel-call.movement-services";

const UnclaimMovement: React.FC<UnclaimMovementProps> = ({ movementFlowData }) => {
  const service = "bff-vessel-call";
  const { notify } = useNotification();
  const { t } = useTranslation();
  const { vesselId, vesselCallId } = useOutletContext<{ vesselId: string; vesselCallId: string }>();

  const queryClient = useQueryClient();
  const unclaimMovementMutation = useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "MovementService",
          action: "unclaim_movement_service",
          entityType: "movement_service",
        },
        (transactionId) =>
          unclaimMovementById({
            service,
            id,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
      // Invalidate and refetch the preview
      queryClient.invalidateQueries({
        queryKey: ["movement-service-preview", movementFlowData?.id],
      });
      // Force immediate refetch since component is already mounted
      await queryClient.refetchQueries({
        queryKey: ["movement-service-preview", movementFlowData?.id],
      });
      notify({
        title: t("vessel-call.movement-services.movement-unclaimed"),
        type: "success",
      });
    },
    onError: (error: Error) =>
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      }),
  });

  if (!movementFlowData) {
    return null;
  }

  if (!movementFlowData.task) {
    return null;
  }

  const isActionAvailable = canUserUnclaim(
    movementFlowData.task.status,
    movementFlowData.task.claimOwnerName,
  );

  const handleUnclaimClick = () => {
    unclaimMovementMutation.mutate(movementFlowData.id);
  };
  return (
    <UnclaimButton
      langBase={LANG_BASE}
      permission="canClaimMovementService"
      isActionAvailable={isActionAvailable}
      onUnclaim={handleUnclaimClick}
    />
  );
};
export default UnclaimMovement;
